import { Injectable, OnDestroy } from '@angular/core';
import * as LDClient from 'launchdarkly-js-client-sdk';
import { from, Observable, of, Subject } from 'rxjs';
import { catchError, filter, mergeMap, switchMap } from 'rxjs/operators';

import { RuntimeConfigService } from './runtime-config';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService implements OnDestroy {
  private readonly flagsSubject = new Subject<string>();
  private ldClient!: LDClient.LDClient;

  constructor(private readonly runtimeConfigService: RuntimeConfigService) {
    this.initializeClient();
  }

  private async initializeClient() {
    const clientSideId =
      this.runtimeConfigService.prequalConfig?.LAUNCH_DARKLY_CLIENT_ID;
    const defaultContext = { anonymous: true };

    if (!this.ldClient) {
      this.ldClient = LDClient.initialize(clientSideId, defaultContext);

      try {
        await this.ldClient.waitForInitialization();

        this.ldClient.on('change', (changes) => {
          Object.keys(changes).forEach((flagKey) => {
            this.flagsSubject.next(flagKey);
          });
        });
      } catch (err) {
        console.error('LaunchDarkly client failed to initialize:', err);
      }
    }
  }

  watchEnabledFlag(flagName: string): Observable<boolean> {
    return this.flagsSubject.pipe(
      filter((key) => key === flagName),
      mergeMap(() => this.isEnabled(flagName))
    );
  }

  isEnabled(flagName: string): Observable<boolean> {
    return from(this.ldClient.waitForInitialization()).pipe(
      switchMap(() => of(this.ldClient.variation(flagName, false))),
      catchError((error) => {
        console.error(`Error evaluating feature flag '${flagName}':`, error);
        return of(false);
      })
    );
  }

  ngOnDestroy() {
    if (this.ldClient) {
      this.ldClient.close();
    }
    this.flagsSubject.complete();
  }
}
